import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconMapPin, IconUser } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { StatusChip } from "../../../../components/common/chip";
import type { OrderStatus } from "../../../../components/common/chip";
import { PartnerAddressCard } from "../../../../components/common/partner-address-card";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { SellerOrderStatusBanner } from "../components/seller-order-status-banner";
import { SellerOrderTimeline } from "../components/seller-order-timeline";
import { SellerOrderSidebar } from "../components/seller-order-sidebar";
import { SellerOrderInvoicesCard } from "../components/seller-order-invoices-card";
import { SellerOrderItemsCard } from "../components/seller-order-items-card";
import { SellerOrderDeliveryProof } from "../components/seller-order-delivery-proof";
import { MarkAsShippedModal } from "../components/mark-as-shipped-modal";
import { MarkAsDeliveredModal } from "../components/mark-as-delivered-modal";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { usePurchaseOrderDetails } from "../../hooks/usePurchaseOrderDetails";
import { mapPoStatus } from "../../utils/po-status";
import { PurchaseOrderNotFound } from "../components/purchase-order-not-found";
import { getPurchaseOrderCurrency } from "../../utils/purchase-order-currency";
import { purchaseOrdersService } from "../../../api_client";
import { mutate } from "swr";
import { mutateByPrefix } from "../../../../swr/useApi";
import { OrderShipmentsCard } from "../../../../components/common/order-shipments-card";

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FORMATTER.format(d);
}

function toApiDateTime(dateInput: string): string {
  const [year, month, day] = dateInput.split("-").map(Number);
  const localDate = new Date(year, month - 1, day, 9, 0, 0);

  const yyyy = localDate.getFullYear();
  const mm = String(localDate.getMonth() + 1).padStart(2, "0");
  const dd = String(localDate.getDate()).padStart(2, "0");
  const hh = String(localDate.getHours()).padStart(2, "0");
  const min = String(localDate.getMinutes()).padStart(2, "0");
  const ss = String(localDate.getSeconds()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

export function SellerOrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shippedModalOpen, setShippedModalOpen] = useState(false);
  const [deliveredModalOpen, setDeliveredModalOpen] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<OrderStatus | null>(null);

  const purchaseOrderId = Number(id ?? 0);
  const { data: po, error, isLoading } = usePurchaseOrderDetails(purchaseOrderId);

  if (isLoading) {
    return <ShimmerLoader message="Fetching order details…" lines={8} />;
  }
  if (error || !po) {
    return <PurchaseOrderNotFound />;
  }

  // The details DTO now exposes `dropshipping[]`, so `mapPoStatus` can derive
  // "inTransit" again after a refresh instead of relying only on optimism.
  const orderStatus = optimisticStatus ?? mapPoStatus(po);
  const isDelivered = orderStatus === "delivered" || orderStatus === "validated";

  const orderRef = po.name ?? `#${po.id}`;
  const placedDate = formatDate(po.dateOrder);
  const expectedShip = formatDate(po.datePlanned ?? po.deliveredAt);
  const shipmentDates = (po.dropshipping ?? [])
    .map((shipment) => shipment.scheduledDate ?? shipment.dateDeadline)
    .filter((value): value is string => !!value);
  const earliestShipmentDate = shipmentDates.reduce<string | null>((earliest, current) => {
    if (!earliest) return current;
    return new Date(current).getTime() < new Date(earliest).getTime() ? current : earliest;
  }, null);
  const shippedDate = formatDate(earliestShipmentDate ?? po.datePlanned);
  const destAddress = po.destAddress;
  const currency = getPurchaseOrderCurrency(po.partner ?? po.destAddress);

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:px-4 max-md:py-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghostAccent"
          size="sm"
          leftIcon={<IconArrowLeft size={18} />}
          onClick={() => navigate(AppRoutes.PURCHASE_ORDERS)}
          className="!h-auto !px-0 !py-0"
        >
          <Heading variant="h3" tone="inherit">
            Back to Orders
          </Heading>
        </Button>
        {/* Phones: the order reference is repeated right below. */}
        <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
        <Heading variant="h3" tone="dark" className="max-md:hidden">
          {orderRef}
        </Heading>
      </div>

      <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-2">
        <Heading variant="h3" tone="dark">{orderRef}</Heading>
        <StatusChip
          status={orderStatus}
          label={orderStatus === "processing" ? "Order Placed" : undefined}
          className={
            orderStatus === "processing"
              ? "!bg-accentGreenTintBg !text-accentGreenDark border border-dashed border-accentGreen py-0.5"
              : ""
          }
        />
        <Text variant="bodySm" tone="muted">
          {orderStatus === "validated"
            ? `Validated: ${placedDate}`
            : orderStatus === "delivered"
              ? `Delivered: ${formatDate(po.deliveredAt)} · Awaiting validation`
              : orderStatus === "inTransit"
                ? `Shipped: ${shippedDate}`
                : `Placed: ${placedDate}`}
      </Text>
      </div>

      <SellerOrderStatusBanner status={orderStatus} placedAt={po.dateOrder} />

      {isDelivered && (
        <SellerOrderInvoicesCard
          swrKey={`purchase-order-details-${purchaseOrderId}`}
          purchaseOrderId={purchaseOrderId}
          status={orderStatus}
          sellerInvoice={
            (po as unknown as {
              sellerInvoice?: {
                id: number;
                name: string;
                mimetype: string;
                url: string;
              } | null;
            }).sellerInvoice
          }
          vendorBill={
            (po as unknown as {
              vendorBill?: {
                id: number;
                name: string;
                previewUrl: string | null;
              } | null;
            }).vendorBill
          }
        />
      )}

      {/* Phones: one column, status + actions card first. */}
      <div className="flex items-start gap-6 max-md:flex-col-reverse max-md:items-stretch">
        <div className="flex min-w-0 flex-[2] flex-col gap-4">
          <div className="flex flex-wrap gap-4">
            <PartnerAddressCard
              title="Delivery Address"
              icon={<IconMapPin size={18} />}
              partner={destAddress}
            />
            {!isDelivered && (
              <PartnerAddressCard
                title="Customer"
                icon={<IconUser size={18} />}
                partner={po.partner}
              />
            )}
          </div>

          <SellerOrderItemsCard items={po.orderLines ?? []} status={orderStatus} />

          {isDelivered && (
            <OrderShipmentsCard shipments={po.dropshipping} title="Shipments" />
          )}

          {isDelivered && (
            <SellerOrderDeliveryProof
              attachments={po.dropshipping?.flatMap((shipment) => shipment.attachments ?? []) ?? []}
              deliveredAt={po.deliveredAt}
            />
          )}

          <SellerOrderTimeline
            status={orderStatus}
            orderedAt={po.dateOrder}
            shippedAt={earliestShipmentDate ?? po.datePlanned}
            deliveredAt={po.deliveredAt}
          />
        </div>

        <div className="flex w-72 shrink-0 flex-col gap-4 max-md:w-full">
          <SellerOrderSidebar
            status={orderStatus}
            totalValue={po.amountTotal ?? 0}
            itemCount={po.orderLines?.length ?? 0}
            expectedShip={expectedShip}
            currency={currency}
            paymentStatus={
              ((po as unknown as { vendorBill?: { paymentState?: string | null } | null }).vendorBill?.paymentState === "paid")
                ? "Paid"
                : isDelivered
                  ? "Awaiting Payment"
                  : undefined
            }
            onMarkShipped={() => setShippedModalOpen(true)}
            onMarkDelivered={() => setDeliveredModalOpen(true)}
          />
        </div>
      </div>

      <MarkAsDeliveredModal
        isOpen={deliveredModalOpen}
        orderId={orderRef}
        onClose={() => setDeliveredModalOpen(false)}
        onConfirm={async (imageFile) => {
          try {
            await purchaseOrdersService.markPurchaseOrderDelivered(purchaseOrderId, imageFile);
            setOptimisticStatus("delivered");
            showSuccessToast("Order marked as delivered.");
            await mutate(`purchase-order-details-${purchaseOrderId}`);
            await mutateByPrefix("purchase-orders");
          } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to mark order as delivered.";
            showErrorToast(message);
            throw error;
          }
        }}
      />

      <MarkAsShippedModal
        isOpen={shippedModalOpen}
        orderId={orderRef}
        onClose={() => setShippedModalOpen(false)}
        onConfirm={async ({ shipDate, estimatedDelivery, carrier, trackingNumber, trackingLink, note }) => {
          await purchaseOrdersService.confirmPurchaseOrder(purchaseOrderId, {
            date_planned: toApiDateTime(shipDate),
            estimatedDelivery: estimatedDelivery ? toApiDateTime(estimatedDelivery) : undefined,
            deliveryUrl: trackingLink?.trim() || undefined,
            carrier: carrier?.trim() || undefined,
            trackingNumber: trackingNumber?.trim() || undefined,
            note: note?.trim() || undefined,
          });

          setOptimisticStatus("inTransit");
          showSuccessToast("Order marked as shipped.");
          await mutate(`purchase-order-details-${purchaseOrderId}`);
          await mutateByPrefix("purchase-orders");
        }}
      />
    </div>
  );
}

export default SellerOrderDetailsPage;
